import type { AppState, PlotSeries, ScientificFrame } from '../science/types';

interface PlotCallbacks {
  onSpectrumFrequency?: (log10Hz: number) => void;
  onCorrelationX?: (xValue: number) => void;
  onQuadratureAngle?: (angleDegrees: number) => void;
  onSliceProbe?: (probe: 'A' | 'B') => void;
}

interface PlotDimensions {
  width: number;
  height: number;
}

const pixelRatio = (): number => Math.min(window.devicePixelRatio || 1, 2);

const setupCanvas = (canvas: HTMLCanvasElement): CanvasRenderingContext2D => {
  const rect = canvas.getBoundingClientRect();
  const ratio = pixelRatio();
  const width = Math.max(96, Math.round(rect.width * ratio));
  const height = Math.max(88, Math.round(rect.height * ratio));
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D context unavailable.');
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  return context;
};

const css = (name: string, fallback: string): string => getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

const clear = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement): PlotDimensions => {
  const ratio = pixelRatio();
  const width = canvas.width / ratio;
  const height = canvas.height / ratio;
  ctx.clearRect(0, 0, width, height);
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, css('--plot-bg-top', 'rgba(3, 19, 34, 0.99)'));
  gradient.addColorStop(1, css('--plot-bg-bottom', 'rgba(1, 7, 13, 1)'));
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  return { width, height };
};

const plotArea = (width: number, height: number) => ({
  left: Math.min(31, width * 0.2),
  top: 8,
  right: Math.max(38, width - 8),
  bottom: Math.max(22, height - 20)
});

const axes = (ctx: CanvasRenderingContext2D, width: number, height: number): void => {
  const area = plotArea(width, height);
  ctx.strokeStyle = 'rgba(135, 174, 205, .32)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(area.left, area.top);
  ctx.lineTo(area.left, area.bottom);
  ctx.lineTo(area.right, area.bottom);
  ctx.stroke();
  ctx.strokeStyle = css('--plot-grid', 'rgba(135, 174, 205, .095)');
  for (let i = 1; i < 5; i += 1) {
    const y = area.top + ((area.bottom - area.top) * i) / 5;
    ctx.beginPath();
    ctx.moveTo(area.left, y);
    ctx.lineTo(area.right, y);
    ctx.stroke();
  }
  for (let i = 1; i < 5; i += 1) {
    const x = area.left + ((area.right - area.left) * i) / 5;
    ctx.beginPath();
    ctx.moveTo(x, area.top);
    ctx.lineTo(x, area.bottom);
    ctx.stroke();
  }
};

const marker = (ctx: CanvasRenderingContext2D, width: number, height: number, normalizedX?: number): void => {
  if (normalizedX === undefined || !Number.isFinite(normalizedX)) return;
  const area = plotArea(width, height);
  const x = area.left + Math.max(0, Math.min(1, normalizedX)) * (area.right - area.left);
  ctx.save();
  ctx.strokeStyle = css('--warning', '#f2bf55');
  ctx.globalAlpha = 0.82;
  ctx.setLineDash([3, 4]);
  ctx.beginPath();
  ctx.moveTo(x, area.top);
  ctx.lineTo(x, area.bottom);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = css('--warning', '#f2bf55');
  ctx.beginPath();
  ctx.arc(x, area.bottom, 2.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

const liveSweep = (ctx: CanvasRenderingContext2D, width: number, height: number, phase: number, enabled: boolean): number => {
  const area = plotArea(width, height);
  const normalized = enabled ? (phase % 1 + 1) % 1 : 0.5;
  const x = area.left + normalized * (area.right - area.left);
  const gradient = ctx.createLinearGradient(x - 18, 0, x + 18, 0);
  gradient.addColorStop(0, 'rgba(61, 169, 255, 0)');
  gradient.addColorStop(0.5, css('--plot-sweep', 'rgba(61, 169, 255, .22)'));
  gradient.addColorStop(1, 'rgba(61, 169, 255, 0)');
  ctx.save();
  ctx.fillStyle = gradient;
  ctx.fillRect(x - 18, area.top, 36, area.bottom - area.top);
  ctx.strokeStyle = 'rgba(132, 210, 255, .52)';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(x, area.top);
  ctx.lineTo(x, area.bottom);
  ctx.stroke();
  ctx.restore();
  return normalized;
};

const extent = (series: PlotSeries[]): { xMin: number; xMax: number; yMin: number; yMax: number } => {
  const points = series.flatMap((entry) => entry.values);
  const xs = points.map((point) => point.x).filter(Number.isFinite);
  const ys = points.map((point) => point.y).filter(Number.isFinite);
  const xMin = Math.min(...xs, 0);
  const xMax = Math.max(...xs, 1);
  const yMin = Math.min(...ys, 0);
  const yMax = Math.max(...ys, 1);
  return { xMin, xMax, yMin, yMax };
};

const plotLines = (
  canvas: HTMLCanvasElement,
  series: PlotSeries[],
  markerX: number | undefined,
  phase: number,
  animated: boolean
): void => {
  const ctx = setupCanvas(canvas);
  const { width, height } = clear(ctx, canvas);
  axes(ctx, width, height);
  const area = plotArea(width, height);
  const colors = [css('--accent', '#3ca7ff'), css('--accent-soft', '#8d56ff'), '#f1b754'];
  const bounds = extent(series);
  series.forEach((entry, seriesIndex) => {
    ctx.strokeStyle = colors[seriesIndex % colors.length] ?? '#fff';
    ctx.lineWidth = seriesIndex === 0 ? 1.5 : 1.1;
    ctx.globalAlpha = seriesIndex === 0 ? 0.9 : 0.76;
    ctx.beginPath();
    entry.values.forEach((point, index) => {
      const nx = (point.x - bounds.xMin) / Math.max(Number.MIN_VALUE, bounds.xMax - bounds.xMin);
      const ny = (point.y - bounds.yMin) / Math.max(Number.MIN_VALUE, bounds.yMax - bounds.yMin);
      const x = area.left + nx * (area.right - area.left);
      const y = area.top + (1 - ny) * (area.bottom - area.top);
      if (index === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.stroke();
  });
  ctx.globalAlpha = 1;
  marker(ctx, width, height, markerX);
  const sweepX = liveSweep(ctx, width, height, phase, animated);
  const primary = series[0]?.values ?? [];
  if (primary.length > 1) {
    const index = Math.min(primary.length - 1, Math.floor(sweepX * (primary.length - 1)));
    const point = primary[index];
    if (point) {
      const nx = (point.x - bounds.xMin) / Math.max(Number.MIN_VALUE, bounds.xMax - bounds.xMin);
      const ny = (point.y - bounds.yMin) / Math.max(Number.MIN_VALUE, bounds.yMax - bounds.yMin);
      ctx.fillStyle = css('--accent', '#3ca7ff');
      ctx.beginPath();
      ctx.arc(area.left + nx * (area.right - area.left), area.top + (1 - ny) * (area.bottom - area.top), 2.8, 0, Math.PI * 2);
      ctx.fill();
    }
  }
};

const plotScatter = (
  canvas: HTMLCanvasElement,
  points: Array<{ x: number; y: number }>,
  angleDegrees: number,
  phase: number,
  animated: boolean
): void => {
  const ctx = setupCanvas(canvas);
  const { width, height } = clear(ctx, canvas);
  axes(ctx, width, height);
  const area = plotArea(width, height);
  const maxAbs = Math.max(2.5, ...points.flatMap((point) => [Math.abs(point.x), Math.abs(point.y)]));
  const accent = css('--accent', '#3ca7ff');
  const accentSoft = css('--accent-soft', '#8d56ff');
  const pulse = animated ? 0.84 + Math.sin(phase * Math.PI * 2) * 0.1 : 0.88;
  points.forEach((point, index) => {
    const x = area.left + ((point.x / maxAbs + 1) / 2) * (area.right - area.left);
    const y = area.top + (1 - (point.y / maxAbs + 1) / 2) * (area.bottom - area.top);
    ctx.globalAlpha = (0.1 + (index % 7) * 0.042) * pulse;
    ctx.fillStyle = index % 2 === 0 ? accent : accentSoft;
    ctx.beginPath();
    ctx.arc(x, y, 1.05, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.globalAlpha = 0.8;
  const centerX = (area.left + area.right) / 2;
  const centerY = (area.top + area.bottom) / 2;
  const angle = angleDegrees * Math.PI / 180;
  const length = Math.min(area.right - area.left, area.bottom - area.top) * 0.43;
  ctx.strokeStyle = css('--warning', '#f2bf55');
  ctx.beginPath();
  ctx.moveTo(centerX - Math.cos(angle) * length, centerY + Math.sin(angle) * length);
  ctx.lineTo(centerX + Math.cos(angle) * length, centerY - Math.sin(angle) * length);
  ctx.stroke();
  ctx.globalAlpha = 1;
  liveSweep(ctx, width, height, phase * 0.72, animated);
};

const plotSlice = (
  canvas: HTMLCanvasElement,
  values: number[][],
  selectedProbe: 'A' | 'B',
  phase: number,
  animated: boolean
): void => {
  const ctx = setupCanvas(canvas);
  const { width, height } = clear(ctx, canvas);
  const rows = values.length;
  const cols = values[0]?.length ?? 0;
  if (!rows || !cols) return;
  const flat = values.flat();
  const maximum = Math.max(...flat.map(Math.abs), Number.MIN_VALUE);
  const cellWidth = width / cols;
  const cellHeight = height / rows;
  const hueBase = Number.parseFloat(css('--slice-hue', '205')) || 205;
  const phaseShift = animated ? Math.sin(phase * Math.PI * 2) * 0.08 : 0;
  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < cols; x += 1) {
      const value = values[y]?.[x] ?? 0;
      const normalized = Math.max(0, Math.min(1, 0.5 + value / (2 * maximum) + phaseShift));
      ctx.fillStyle = `hsl(${hueBase + normalized * 105} 80% ${13 + normalized * 58}%)`;
      ctx.fillRect(x * cellWidth, y * cellHeight, Math.ceil(cellWidth + 0.5), Math.ceil(cellHeight + 0.5));
    }
  }
  const selectedX = selectedProbe === 'A' ? 2 : width / 2;
  ctx.strokeStyle = selectedProbe === 'A' ? css('--accent', '#3ca7ff') : css('--accent-soft', '#8d56ff');
  ctx.lineWidth = 2;
  ctx.strokeRect(selectedX, 2, width / 2 - 4, height - 4);
  const scanY = animated ? ((phase % 1 + 1) % 1) * height : height / 2;
  const scan = ctx.createLinearGradient(0, scanY - 12, 0, scanY + 12);
  scan.addColorStop(0, 'rgba(255,255,255,0)');
  scan.addColorStop(0.5, 'rgba(255,255,255,.18)');
  scan.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = scan;
  ctx.fillRect(0, scanY - 12, width, 24);
};

const normalizedClickX = (event: MouseEvent, canvas: HTMLCanvasElement): number => {
  const rect = canvas.getBoundingClientRect();
  return Math.max(0, Math.min(1, (event.clientX - rect.left) / Math.max(1, rect.width)));
};

export class MiniPlots {
  private spectrum = document.querySelector<HTMLCanvasElement>('#plot-spectrum');
  private quadrature = document.querySelector<HTMLCanvasElement>('#plot-quadrature');
  private correlation = document.querySelector<HTMLCanvasElement>('#plot-correlation');
  private detector = document.querySelector<HTMLCanvasElement>('#plot-detector');
  private slice = document.querySelector<HTMLCanvasElement>('#plot-slice');
  private currentFrame: ScientificFrame | null = null;
  private currentState: Readonly<AppState> | null = null;
  private animationId = 0;
  private lastTimestamp = 0;
  private lastRender = 0;
  private phase = 0;

  constructor(callbacks: PlotCallbacks = {}) {
    this.spectrum?.addEventListener('click', (event) => {
      const band = this.currentFrame?.frequencyBand;
      const normalized = normalizedClickX(event, this.spectrum as HTMLCanvasElement);
      const frequency = band ? band.lowerHz + normalized * (band.upperHz - band.lowerHz) : 10 ** (6 + normalized * 10);
      callbacks.onSpectrumFrequency?.(Math.log10(Math.max(1, frequency)));
    });
    this.correlation?.addEventListener('click', (event) => {
      const series = this.currentFrame?.correlationSeries[0]?.values ?? [];
      const minimum = series[0]?.x ?? -10;
      const maximum = series.at(-1)?.x ?? 10;
      callbacks.onCorrelationX?.(minimum + normalizedClickX(event, this.correlation as HTMLCanvasElement) * (maximum - minimum));
    });
    this.quadrature?.addEventListener('click', (event) => {
      const canvas = this.quadrature as HTMLCanvasElement;
      const rect = canvas.getBoundingClientRect();
      const x = event.clientX - rect.left - rect.width / 2;
      const y = rect.height / 2 - (event.clientY - rect.top);
      callbacks.onQuadratureAngle?.((Math.atan2(y, x) * 180 / Math.PI + 360) % 180);
    });
    this.slice?.addEventListener('click', (event) => callbacks.onSliceProbe?.(normalizedClickX(event, this.slice as HTMLCanvasElement) < 0.5 ? 'A' : 'B'));
    const labels = new Map<HTMLCanvasElement | null, string>([
      [this.spectrum, 'Interactive electromagnetic spectrum. Use left and right arrow keys to change the center frequency.'],
      [this.quadrature, 'Interactive quadrature distribution. Use left and right arrow keys to change the squeezing angle.'],
      [this.correlation, 'Interactive two-probe correlation. Use left and right arrow keys to change the relative delay.'],
      [this.detector, 'Animated ideal detector sample series.'],
      [this.slice, 'Animated field snapshot slice. Press A or B to select a probe.']
    ]);
    for (const [canvas, label] of labels) {
      if (!canvas) continue;
      canvas.tabIndex = 0;
      canvas.setAttribute('role', 'img');
      canvas.setAttribute('aria-label', label);
      canvas.setAttribute('aria-keyshortcuts', canvas === this.slice ? 'A B' : canvas === this.detector ? '' : 'ArrowLeft ArrowRight');
    }
    this.spectrum?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const center = Math.log10(Math.max(1, this.currentFrame?.frequencyBand.centerHz ?? 1e12));
      callbacks.onSpectrumFrequency?.(center + (event.key === 'ArrowRight' ? 0.1 : -0.1));
    });
    this.correlation?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const values = this.currentFrame?.correlationSeries[0]?.values ?? [];
      const minimum = values[0]?.x ?? -10;
      const maximum = values.at(-1)?.x ?? 10;
      const step = Math.max((maximum - minimum) / 100, Number.EPSILON);
      callbacks.onCorrelationX?.(this.correlationX(this.currentState, this.currentFrame) + (event.key === 'ArrowRight' ? step : -step));
    });
    this.quadrature?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const angle = (this.currentState?.scientific.squeezingAngle ?? 0) + (event.key === 'ArrowRight' ? 5 : -5);
      callbacks.onQuadratureAngle?.((angle + 180) % 180);
    });
    this.slice?.addEventListener('keydown', (event) => {
      if (event.key.toLowerCase() !== 'a' && event.key.toLowerCase() !== 'b') return;
      event.preventDefault();
      callbacks.onSliceProbe?.(event.key.toLowerCase() === 'a' ? 'A' : 'B');
    });
    this.animationId = requestAnimationFrame(this.animate);
  }

  update(frame: ScientificFrame, state: Readonly<AppState>): void {
    this.currentFrame = frame;
    this.currentState = state;
    this.spectrum?.setAttribute('aria-label', `Interactive ${frame.plotSemantics.spectrumTitle}: ${frame.plotSemantics.spectrumSubtitle}. Use left and right arrow keys to change the selected frequency.`);
    this.quadrature?.setAttribute('aria-label', `Interactive ${frame.plotSemantics.quadratureTitle}. Use left and right arrow keys to change the selected quadrature angle.`);
    this.correlation?.setAttribute('aria-label', `Interactive ${frame.plotSemantics.correlationTitle}. Use left and right arrow keys to change the selected horizontal coordinate.`);
    this.detector?.setAttribute('aria-label', `Animated ${frame.plotSemantics.detectorTitle} series generated by ${frame.modelId}.`);
    this.slice?.setAttribute('aria-label', `Animated ${frame.plotSemantics.sliceTitle}. Press A or B to select a probe.`);
    this.render();
  }

  step(deltaSeconds = 1 / 15): void {
    this.phase += Math.max(0, deltaSeconds) * (this.currentState?.visualization.animationSpeed ?? 1) * 0.22;
    this.render();
  }

  private animate = (timestamp: number): void => {
    this.animationId = requestAnimationFrame(this.animate);
    const state = this.currentState;
    if (!state || !this.currentFrame) return;
    const delta = this.lastTimestamp ? Math.min(0.08, (timestamp - this.lastTimestamp) / 1000) : 0;
    this.lastTimestamp = timestamp;
    if (!state.visualization.animationPaused) {
      const reducedFactor = state.visualization.reducedMotion ? 0.2 : 1;
      this.phase += delta * state.visualization.animationSpeed * reducedFactor * 0.22;
    }
    const targetInterval = state.visualization.reducedMotion ? 160 : 70;
    if (timestamp - this.lastRender >= targetInterval) {
      this.render();
      this.lastRender = timestamp;
    }
  };

  private render(): void {
    const frame = this.currentFrame;
    const state = this.currentState;
    if (!frame || !state) return;
    const animated = !state.visualization.animationPaused;
    const spectrumMarker = (frame.frequencyBand.centerHz - frame.frequencyBand.lowerHz) / Math.max(1, frame.frequencyBand.upperHz - frame.frequencyBand.lowerHz);
    if (this.spectrum) plotLines(this.spectrum, frame.spectrum, spectrumMarker, this.phase, animated);
    if (this.quadrature) plotScatter(this.quadrature, frame.quadrature, state.scientific.squeezingAngle, this.phase, animated);
    if (this.correlation) {
      const values = frame.correlationSeries[0]?.values ?? [];
      const min = values[0]?.x ?? -10;
      const max = values.at(-1)?.x ?? 10;
      const activeX = this.correlationX(state, frame);
      plotLines(this.correlation, frame.correlationSeries, (activeX - min) / Math.max(Number.MIN_VALUE, max - min), this.phase * 0.82, animated);
    }
    if (this.detector) plotLines(this.detector, frame.detectorSeries, undefined, this.phase * 1.38, animated);
    if (this.slice) plotSlice(this.slice, frame.fieldSlice, state.selectedProbe, this.phase * 0.9, animated);
  }

  private correlationX(state: Readonly<AppState> | null, frame: ScientificFrame | null): number {
    if (!state || !frame) return 0;
    switch (state.era) {
      case '2010s': return (10 ** state.scientific.frequencyLog10 - (10 ** state.scientific.modulationFrequencyLog10) / 2) / 1e9;
      case '1990s-2000s': return state.scientific.probeDelay / 1000;
      case '1970s-1980s': return 10 ** state.scientific.integrationTimeLog10 * 1e9;
      case '1950s-1960s': return 10 ** state.scientific.boundarySeparationLog10 * 1e9;
      case '1940s': return 10 ** state.scientific.frequencyLog10 / 1e6;
      case 'modern':
      default: return state.scientific.probeDelay;
    }
  }

  dispose(): void {
    cancelAnimationFrame(this.animationId);
  }
}
